// SPDX-License-Identifier: BUSL-1.1

/**
 * Reusable sticky topbar for dashboard pages.
 *
 * Renders a 48px-tall sticky header with a title/subtitle block on the left
 * and a right-aligned actions cluster containing an optional live indicator,
 * caller-provided action slot, and a refresh button with a one-shot spin
 * animation.
 *
 */

import { useState, type ReactElement, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface DashboardTopbarProps {
  /** Main page title, e.g. "Dashboard". */
  title: string;
  /** Optional subtitle rendered beside the title, e.g. "Operations Overview". */
  subtitle?: string;
  /** Handler for the refresh button. If omitted, the refresh button is hidden. */
  onRefresh?: () => void;
  /** When true, shows a pulsing green dot labelled "live". */
  isLive?: boolean;
  /** Extra action nodes rendered on the right, before the refresh button. */
  actions?: ReactNode;
}

/**
 * Sticky single-line dashboard topbar.
 *
 * Self-contained primitive — holds no global state and performs no data
 * fetching. Callers wire the refresh handler and optional action buttons.
 */
export function DashboardTopbar({
  title,
  subtitle,
  onRefresh,
  isLive = false,
  actions,
}: DashboardTopbarProps): ReactElement {
  const [spinning, setSpinning] = useState(false);

  const handleRefreshClick = (): void => {
    setSpinning(true);
    onRefresh?.();
    setTimeout(() => setSpinning(false), 500);
  };

  return (
    <div className="sticky top-0 z-10 flex h-12 items-center gap-3 border-b border-border bg-background px-4">
      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="truncate text-sm font-semibold text-neutral-100">
          {title}
        </h1>
        {subtitle ? (
          <span className="truncate text-xs text-neutral-500">{subtitle}</span>
        ) : null}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {isLive ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-400">
            <span
              aria-hidden="true"
              className="pulse-green inline-block size-1.5 rounded-full bg-green-500"
            />
            live
          </span>
        ) : null}

        {actions}

        {onRefresh ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRefreshClick}
            aria-label="Refresh"
          >
            <RefreshCw
              className="size-3.5"
              style={{
                transition: 'transform 500ms ease',
                transform: spinning ? 'rotate(360deg)' : 'rotate(0deg)',
              }}
            />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
